/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useRef } from 'react';
import { Trans } from 'react-i18next';
import {
  Genre,
  Momentum,
  Peakairplay,
  Region,
  Rotation,
} from '../../assets/customIcons';
import { SongLibraryInsights } from '../Library/Library.slice';
import { trackEvent } from '../../utils/segmentService';
import { CLICK_EVENT } from '../../consts/analytics_consts';
import {
  InsightsContainerDesktop,
  InsightsCardDesktop,
  InsightsWrapperDesktop,
  CardType,
  CardTypeWrapper,
  CardStatus,
  OverviewWrapper,
  OverviewInsightsStyle,
} from './SmartInsights.styles';

import OverviewInsightsSkeleton from './OverviewInsightsSkeleton';
import { renderGenreTransContent } from './renderGenreTransContent';
import { renderRegionTransContent } from './renderRegionTransContent';

interface OverviewInsightsDesktopProps {
  isLoading?: boolean;
  insightCounts: SongLibraryInsights;
  topRegions?: string[];
  topGenres?: string[];
  onInsightClick: (insightType: string, detail?: string) => void;
  selectedInsightType?: string;
}

const EDGE_BAND = 28; // px from left/right that triggers the snap
const COOLDOWN_MS = 400; // avoid retrigger spam while animating
const STEP_BY_CARDS = true; // If true, each step moves by N cards, if false, each step moves by one viewport
const CARDS_PER_STEP = 1;

const OverviewInsightsDesktop: React.FC<OverviewInsightsDesktopProps> = ({
  isLoading,
  insightCounts,
  topRegions = [],
  topGenres = [],
  onInsightClick,
  selectedInsightType,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cooldownRef = useRef<number>(0);
  const showSkeleton = isLoading ?? false;

  // Handling insight click with tracking
  const handleInsightClickWithTracking = (
    insightType: string,
    detail?: string
  ) => {
    trackEvent(CLICK_EVENT, {
      type: insightType,
      source: 'dashboard_library_page',
      ...(detail ? { insight_detail: detail } : {}),
    });
    onInsightClick(insightType, detail);
  };

  // Handling desktop pointer detection
  const isDesktopPointer = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Clamping function
  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(v, max));

  // Handling cards
  const getCardDelta = (el: HTMLDivElement) => {
    const children = Array.from(el.children) as HTMLElement[];
    if (children.length < 2) return 0;
    let delta = 0;
    // distance between consecutive items' left edges
    for (let i = 0; i < children.length - 1; i += 1) {
      const r1 = children[i].getBoundingClientRect();
      const r2 = children[i + 1].getBoundingClientRect();
      delta = Math.abs(r2.left - r1.left);
      if (delta > 0) break;
    }
    return delta;
  };

  // Handling step target
  const getStepTarget = (el: HTMLDivElement, dir: -1 | 1) => {
    const maxLeft = el.scrollWidth - el.clientWidth;
    const stepDistance = STEP_BY_CARDS
      ? Math.max(1, Math.round(getCardDelta(el) * CARDS_PER_STEP))
      : el.clientWidth;

    const target = clamp(el.scrollLeft + dir * stepDistance, 0, maxLeft);
    return target;
  };

  // Edge hover auto-scroll
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;

    // desktop
    if (!isDesktopPointer()) return;

    // only when there's something to scroll
    if (el.scrollWidth <= el.clientWidth + 1) return;

    // throttle while animating
    if (Date.now() < cooldownRef.current) return;

    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const atStart = el.scrollLeft <= 2;
    const atEnd =
      Math.abs(el.scrollLeft - (el.scrollWidth - el.clientWidth)) <= 2;

    if (x <= EDGE_BAND && !atStart) {
      const target = getStepTarget(el, -1);
      if (Math.abs(target - el.scrollLeft) > 1) {
        el.scrollTo({ left: target, behavior: 'smooth' });
        cooldownRef.current = Date.now() + COOLDOWN_MS;
      }
    } else if (x >= rect.width - EDGE_BAND && !atEnd) {
      const target = getStepTarget(el, 1);
      if (Math.abs(target - el.scrollLeft) > 1) {
        el.scrollTo({ left: target, behavior: 'smooth' });
        cooldownRef.current = Date.now() + COOLDOWN_MS;
      }
    }
  }, []);

  // Handling clear down
  const clearCooldown = () => {
    cooldownRef.current = 0;
  };

  if (showSkeleton) {
    return <OverviewInsightsSkeleton />;
  }

  const hasMomentumInsights =
    Number(insightCounts.momentumInsightsGain) > 0 ||
    Number(insightCounts.momentumInsightsStagnant) > 0 ||
    Number(insightCounts.momentumInsightsDown) > 0;

  const isMomentumSelected =
    selectedInsightType === 'momentum_gain' ||
    selectedInsightType === 'momentum_stagnant' ||
    selectedInsightType === 'momentum_slowing';
  const isPeakSelected =
    selectedInsightType === 'peak_morning' ||
    selectedInsightType === 'peak_evening' ||
    selectedInsightType === 'peak_night';
  const isRotationSelected = selectedInsightType === 'rotation';
  const isRegionSelected = selectedInsightType === 'region';
  const isGenreSelected = selectedInsightType === 'genre';

  return (
    <>
      {/* Overview Insights — desktop (display order: Momentum, Peak Airplay, Rotation, Region, Genre) */}
      <InsightsWrapperDesktop
        ref={scrollerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={clearCooldown}
      >
        {/* ---- 1. Momentum ---- */
        /* Up to three states may be shown */}
        {hasMomentumInsights && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop
              className="momentum-card"
              $active={isMomentumSelected}
            >
              <CardTypeWrapper>
                <Momentum />
                <CardType>
                  <Trans i18nKey="MOMENTUM_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Gaining */}
                  {insightCounts.momentumInsightsGain > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('momentum_gain')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_MOMENTUM_GAINING"
                        values={{ count: insightCounts.momentumInsightsGain }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                  {/* Stagnant */}
                  {insightCounts.momentumInsightsStagnant > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('momentum_stagnant')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_MOMENTUM_STAGNANT"
                        values={{
                          count: insightCounts.momentumInsightsStagnant,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                  {/* Slowing */}
                  {insightCounts.momentumInsightsDown > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('momentum_slowing')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_MOMENTUM_SLOWING"
                        values={{ count: insightCounts.momentumInsightsDown }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 2. Peak Airplay ---- */}
        {/* Up to three states may appear */}
        {insightCounts.peakAirplayMorningInsights > 0 && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop
              className="peakairplay-card"
              $active={isPeakSelected}
            >
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Morning */}
                  {insightCounts.peakAirplayMorningInsights > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('peak_morning')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_PEAK_MORNING"
                        values={{
                          count: insightCounts.peakAirplayMorningInsights,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}

                  {/* Evening */}
                  {insightCounts.peakAirplayEveningInsights > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('peak_evening')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_PEAK_EVENING"
                        values={{
                          count: insightCounts.peakAirplayEveningInsights,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}

                  {/* Night */}
                  {insightCounts.peakAirplayNightInsights > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('peak_night')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_PEAK_NIGHT"
                        values={{
                          count: insightCounts.peakAirplayNightInsights,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 3. Rotation ---- */}
        {/* Shows the number of songs that may be in possible rotation. */}
        {insightCounts.rotationInsights > 0 && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop
              className="rotation-card"
              $active={isRotationSelected}
            >
              <CardTypeWrapper>
                <Rotation />
                <CardType>
                  <Trans i18nKey="ROTATION_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Possible rotation */}
                  <OverviewInsightsStyle
                    onClick={() => handleInsightClickWithTracking('rotation')}
                  >
                    <Trans
                      i18nKey="SMART_INSIGHTS_ROTATION_ADDED"
                      values={{ count: insightCounts.rotationInsights }}
                      components={{
                        highlight: (
                          <strong
                            style={{
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          />
                        ),
                      }}
                    />
                  </OverviewInsightsStyle>
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 4. Region ---- */}
        {/* Displays up to the 3 most popular regions. */}
        {insightCounts.regionsInsights > 0 && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop
              className="region-card"
              $active={isRegionSelected}
            >
              <CardTypeWrapper>
                <Region />
                <CardType>
                  <Trans i18nKey="REGION_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Top region */}
                  <OverviewInsightsStyle
                    onClick={() =>
                      handleInsightClickWithTracking('region', topRegions[0])
                    }
                  >
                    {renderRegionTransContent(
                      topRegions,
                      handleInsightClickWithTracking
                    )}
                  </OverviewInsightsStyle>
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}

        {/* ---- 5. Genre ---- */}
        {/* Displays up to the 3 main genres. */}
        {insightCounts.genresInsights > 0 && (
          <InsightsContainerDesktop>
            <InsightsCardDesktop
              className="genre-card"
              $active={isGenreSelected}
            >
              <CardTypeWrapper>
                <Genre />
                <CardType>
                  <Trans i18nKey="GENRE_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  <OverviewInsightsStyle
                    onClick={() =>
                      handleInsightClickWithTracking('genre', topGenres[0])
                    }
                  >
                    {renderGenreTransContent(
                      topGenres,
                      handleInsightClickWithTracking
                    )}
                  </OverviewInsightsStyle>
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardDesktop>
          </InsightsContainerDesktop>
        )}
      </InsightsWrapperDesktop>
    </>
  );
};

export default OverviewInsightsDesktop;
